import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {Link, Tooltip} from '@primer/react'
import type {KeyboardEvent} from 'react'

import styles from './IssueFieldValueBadge.module.css'

const MAX_TEXT_LENGTH = 30

type IssueFieldValueBadgeProps = {
  fieldName: string
  displayValue: string
  href?: string
}

function truncateValue(value: string): string {
  if (value.length <= MAX_TEXT_LENGTH) return value
  return `${value.slice(0, MAX_TEXT_LENGTH)}…`
}

// Stop list-view keyboard navigation from swallowing keystrokes that should
// activate the link (mirrors IssueItemSubIssuesSummary).
function stopKeyPropagation(event: KeyboardEvent<HTMLAnchorElement>) {
  event.stopPropagation()
}

type IssueFieldValueTokenProps = {
  displayValue: string
}

// The bare value token (no link/tooltip/badge wrapper), shared by the inline
// click-to-filter badge and the overflow menu so a field value always renders
// in the same muted "label" form.
export function IssueFieldValueToken({displayValue}: IssueFieldValueTokenProps) {
  return (
    <span className={styles.token} data-testid="issue-field-value-badge">
      {truncateValue(displayValue)}
    </span>
  )
}

export function IssueFieldValueBadge({fieldName, displayValue, href}: IssueFieldValueBadgeProps) {
  const tokenText = truncateValue(displayValue)
  const fullText = `${fieldName}: ${displayValue}`

  if (href) {
    return (
      <ListItemTrailingBadge title={fullText}>
        <Tooltip text={fullText} type="label">
          <Link
            href={href}
            onKeyDown={stopKeyPropagation}
            className={styles.link}
            data-testid="issue-field-value-badge-link"
          >
            <IssueFieldValueToken displayValue={displayValue} />
          </Link>
        </Tooltip>
      </ListItemTrailingBadge>
    )
  }

  return (
    <ListItemTrailingBadge title={fullText}>
      <span className={styles.token} data-testid="issue-field-value-badge">
        <span aria-hidden="true">{tokenText}</span>
        <span className="sr-only">{fullText}</span>
      </span>
    </ListItemTrailingBadge>
  )
}
